import * as THREE from 'three';
import { Input } from './Input';
import { save } from './SaveSystem';
import { bus } from './EventBus';

export type GameMode = 'title' | 'play' | 'build' | 'shop' | 'customize' | 'menu';

export interface Tickable {
  tick(dt: number, elapsed: number): void;
}

export class Game {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly input: Input;
  readonly clock = new THREE.Clock();
  private systems: Tickable[] = [];
  private raf = 0;
  private _mode: GameMode = 'title';

  constructor(private canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: false,
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      500,
    );
    this.camera.position.set(0, 6, 14);

    this.input = new Input(canvas);

    window.addEventListener('resize', this.onResize);
    window.addEventListener('contextmenu', (e) => e.preventDefault());

    save.hydrate();
  }

  register(system: Tickable): void {
    this.systems.push(system);
  }

  get mode(): GameMode { return this._mode; }
  setMode(m: GameMode): void {
    if (m === this._mode) return;
    const prev = this._mode;
    this._mode = m;
    bus.emit('mode:exit', { mode: prev });
    bus.emit('mode:enter', { mode: m });
  }

  start(): void {
    this.clock.start();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      const dt = Math.min(this.clock.getDelta(), 0.05);
      const elapsed = this.clock.elapsedTime;
      for (const sys of this.systems) sys.tick(dt, elapsed);
      this.renderer.render(this.scene, this.camera);
      this.input.endFrame();
    };
    loop();
  }

  stop(): void { cancelAnimationFrame(this.raf); }

  private onResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  };
}
